<template>
  <div>
    <div :class="$style.container">
      <table class="table is-bordered">
        <thead>
          <tr>
            <th>Answer Choices</th>
            <th>{{ siteName }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(value, index) in items" :key="`statistics${index}`">
            <td>{{ Object.keys(value)[0] }}</td>
            <td>
              {{
                criteriaSamplingDataConfiguration.length
                  ? Object.values(value)[0]
                  : '-'
              }}
            </td>
          </tr>
          <tr v-if="items.length === 0">
            <td colspan="2" :class="$style.noDataPlaceholder">
              No data available
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th>Total Data Collected</th>
            <th>{{ totalCollected }}</th>
          </tr>
          <tr v-if="showComplianceRate">
            <th>Compliance Rate</th>
            <th>{{ complianceRate }}</th>
          </tr>
        </tfoot>
      </table>
    </div>
    <div :class="$style.samplingContainer">
      Sampling
      <div :class="$style.samplingTable">
        <table class="table is-bordered">
          <thead>
            <tr v-if="!auditReport">
              <th
                :colspan="
                  samplingConfiguration.samplingMode === 'minAndMax' ? 3 : 2
                "
              >
                {{ selectedPeriod.title }}<br /><br />{{
                  formatDateRange(
                    selectedPeriod.startedAt,
                    selectedPeriod.endedAt,
                  )
                }}
              </th>
            </tr>
            <tr v-else></tr>
            <tr>
              <th
                v-for="(title, titleIndex) in getSamplingTitles"
                :key="titleIndex"
                :class="[titleIndex !== 0 ? $style.columnAlignment : '']"
              >
                {{ title }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(rowData, rowDataIndex) in getSamplingAggregate"
              :key="rowDataIndex"
            >
              <td>{{ rowData.siteName }}</td>
              <td
                v-for="(value, valueIndex) in rowData.values"
                :key="valueIndex"
                :class="$style.columnAlignment"
              >
                {{ value }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { AuditSite } from '../../store/modules/audits/types/audits.types';
import { ReportSamplingDto } from '../../store/modules/projects/types/projects.types';
import {
  getAdHocSampling,
  getConsecutiveMinMaxSampling,
  getDefaultSampling,
} from '../../components/reports/utils/ReportSampling.util';

const dirtyMonths: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

@Component({
  components: {},
})
export default class SingleSiteTable extends Vue {
  @Prop()
  public source!: Array<{ [key: string]: number }>;
  @Prop({ default: 0 })
  public sampleSize!: number;
  @Prop()
  public siteList!: AuditSite[];
  @Prop({ default: true })
  public showComplianceRate!: boolean;
  @Prop()
  public siteSamplingConfiguration!: any[];
  @Prop()
  public samplingConfiguration!: any;
  @Prop({ default: true })
  public isAuditCriteria!: boolean;
  @Prop()
  public criteriaSamplingDataConfiguration!: any;
  @Prop()
  public selectedPeriod!: any;
  @Prop({ default: false })
  public auditReport!: boolean;

  public siteName: string = '';

  public mounted() {
    this.siteName = this.siteList[0].site.name;
  }

  public formatDateRange(startedAt: string, endedAt: any): string {
    if (!endedAt) {
      endedAt = new Date();
    }
    return (
      this.getDate(new Date(startedAt)) +
      ' - ' +
      this.getDate(new Date(endedAt))
    );
  }

  public getDate(newValue: Date): string {
    return (
      newValue.getDate() +
      ' ' +
      dirtyMonths[newValue.getMonth()] +
      ' ' +
      newValue.getFullYear()
    );
  }

  get items(): Array<{ [key: string]: number }> {
    if (Array.isArray(this.source)) {
      return this.source;
    }
    return [];
  }

  get totalCollected(): string {
    let sum: number = 0;
    let sumNa: string = '';
    this.items.map((value) => {
      if (Object.values(value)[0].toString() === '-') {
        return (sumNa = '-');
      } else {
        return (sum += Number(Object.values(value)[0]));
      }
    });
    if (this.criteriaSamplingDataConfiguration.length === 0) {
      return (sumNa = '-');
    }
    if (sumNa === '-') {
      return sumNa;
    } else {
      return sum.toString();
    }
  }

  get totalNACollected(): number {
    let sum: number = 0;
    this.items.map((value, index) => {
      if (index === 2) {
        return (sum += Object.values(value)[0]);
      }
    });
    return sum;
  }

  get complianceRate(): string {
    const positive: any = this.items[0];
    const positiveKey: string = Object.keys(positive)[0];
    if (positive[positiveKey] === 0) {
      return this.criteriaSamplingDataConfiguration.length ? '0%' : '-';
    }
    const rate: number =
      (positive[positiveKey] /
        (Number(this.totalCollected) - this.totalNACollected)) *
        100 || 0;
    if (!rate) {
      return '-';
    }
    return `${rate.toFixed(2)}%`;
  }

  get getSamplingTitles() {
    const samplingTitles: string[] = [];
    samplingTitles.push('Site');
    switch (this.samplingConfiguration.auditSamplingType) {
      case 'adHoc':
        samplingTitles.push('Sampling');
        break;
      case 'consecutive':
        if (this.selectedPeriod.isAudit || this.auditReport) {
          if (this.samplingConfiguration.samplingMode === 'minAndMax') {
            samplingTitles.push('Min.');
            samplingTitles.push('Max.');
          } else {
            samplingTitles.push('Target');
          }
        } else {
          samplingTitles.push('Sampling');
        }
        break;
      default:
        if (this.selectedPeriod.isAudit || this.auditReport) {
          samplingTitles.push('Target');
        } else {
          samplingTitles.push('Sampling');
        }
    }
    return samplingTitles;
  }

  get getSamplingAggregate() {
    const samplingRows: any[] = [];
    switch (this.samplingConfiguration.auditSamplingType) {
      case 'adHoc':
        const dirtyAdHocSamplingRows: ReportSamplingDto[] = getAdHocSampling(
          this.selectedPeriod.isAudit || this.auditReport,
          this.isAuditCriteria,
          this,
          this.siteList,
        );
        samplingRows.push(...dirtyAdHocSamplingRows);
        break;
      case 'consecutive':
        if (this.samplingConfiguration.samplingMode === 'minAndMax') {
          const dirtyConsecutiveMinMaxSamplingRows: ReportSamplingDto[] = getConsecutiveMinMaxSampling(
            this.selectedPeriod.isAudit || this.auditReport,
            this.isAuditCriteria,
            this,
            this.siteList,
          );
          samplingRows.push(...dirtyConsecutiveMinMaxSamplingRows);
        } else {
          const dirtyDefaultConsecutiveSamplingRowsData: ReportSamplingDto[] = getDefaultSampling(
            this.selectedPeriod.isAudit || this.auditReport,
            this.isAuditCriteria,
            this,
            this.siteList,
          );
          samplingRows.push(...dirtyDefaultConsecutiveSamplingRowsData);
        }
        break;
      default:
        const dirtyDefaultSamplingRowsData: ReportSamplingDto[] = getDefaultSampling(
          this.selectedPeriod.isAudit || this.auditReport,
          this.isAuditCriteria,
          this,
          this.siteList,
          this.samplingConfiguration.samplingMode === 'siteBased',
        );
        samplingRows.push(...dirtyDefaultSamplingRowsData);
    }
    return samplingRows;
  }
}
</script>

<style lang="scss" module>
.container {
  width: 100%;
  overflow: auto;
  border-radius: 6px;
  border: 0.5px solid #dbdbdb;
  table {
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
    border-style: hidden;
    background-color: #fbfbfb;
    tr {
      th,
      td {
        padding: 0.75em 0.75em;
        width: auto;
        &:nth-child(2) {
          width: 15%;
          text-align: right;
        }
      }
    }
    thead {
      background: none;
      tr > th {
        padding: 1em 0.75em;
        color: $grey;
        font-weight: 400;
        font-size: 14px;
        color: #9f9f9f;
      }
    }
    tbody {
      tr > td {
        border-top: none;
        border-left: none;
        border-bottom: none;
        font-weight: normal;
      }
    }
    tfoot {
      tr:not(:last-child) {
        th {
          font-weight: 500;
        }
      }
      tr:last-child {
        th {
          font-weight: 800;
        }
      }
    }
  }
}
.samplingContainer {
  margin-top: 20px;
}
.samplingTable {
  width: 100%;
  overflow-x: scroll;
  border-radius: 6px;
  border: 0.5px solid #dbdbdb;
  margin-top: 15px;
  table {
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
    border-style: hidden;
    white-space: nowrap;
    background-color: #fbfbfb;
    thead {
      background: none;
      tr > th {
        padding: 1em 0.75em;
        color: #9f9f9f;
        font-weight: unset;
        font-size: 14px;
        border-top: unset;
      }
      th.columnAlignment {
        text-align: right;
        width: 184px;
      }
    }
    tbody {
      tr > td {
        border-top: none;
        border-left: none;
        border-bottom: none;
        font-weight: normal;
      }
      td.columnAlignment {
        text-align: right;
      }
    }
  }
}
.noDataPlaceholder {
  &#{&} {
    padding: 5rem;
    text-align: center;
    vertical-align: middle;
  }
}
</style>
